import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSEO from "@/hooks/useSEO";

const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
};

const companyInfo = [
  { label: "会社名", labelEn: "Company Name", value: "Stream Solutions Limited" },
  { label: "代表者", labelEn: "Representative", value: "Yuri Yamauchi（山内 ゆり）" },
  { label: "所在地", labelEn: "Address (EN)", value: "Room D, Rm4. 2/F, Sunray Industrial Centre, 610 Cha Kwo Ling Road, Yau Tong, KL." },
  { label: "所在地（中文）", labelEn: "Address (ZH)", value: "香港九龍油塘茶果嶺道610號 生利工業大廈2樓4室" },
  { label: "電話番号", labelEn: "Phone", value: "+852-5321-3455" },
  { label: "事業内容", labelEn: "Business", value: "決済管理・資金管理業務支援、システム開発・提供" },
];

const milestones = [
  { year: "2014", title: "創業", desc: "香港にてStream Solutions Limitedを設立。日本向け決済管理サービスを開始。" },
  { year: "2016", title: "システム開発部門設立", desc: "自社開発チームを組成。独自の照合エンジン開発に着手。" },
  { year: "2018", title: "AI照合エンジン v1.0 リリース", desc: "機械学習を活用した自動消込システムを初リリース。照合精度98%を達成。" },
  { year: "2020", title: "クラウドプラットフォーム移行", desc: "金融グレードのクラウドインフラへ完全移行。24/7稼働体制を確立。" },
  { year: "2022", title: "導入企業100社突破", desc: "累計導入企業数が100社を突破。照合精度99.8%を達成。" },
  { year: "2024", title: "次世代プラットフォーム公開", desc: "リアルタイムAPI連携・自動レポート生成を統合した新プラットフォームを公開。" },
];

const values = [
  { icon: "ri-focus-3-line", num: "99.8%", label: "照合精度", desc: "自社AIエンジンによる業界最高水準の精度" },
  { icon: "ri-speed-line", num: "80%+", label: "工数削減", desc: "自動化による従来業務との比較削減率" },
  { icon: "ri-building-2-line", num: "150+", label: "導入企業", desc: "日本・アジア全域の導入実績" },
  { icon: "ri-time-line", num: "24/7", label: "サポート体制", desc: "365日ノンストップの専任サポート" },
];

// ---- Sections ----

const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  return (
    <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-slate-950">
      {/* Full-bleed background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=hong-kong-night-skyline-victoria-harbour-aerial-view-city-lights-reflection-on-water-dramatic-urban-landscape-financial-district-skyscrapers-dark-moody-atmosphere-no-people&width=1920&height=900&seq=abouthero_v2&orientation=landscape"
          alt="Hong Kong skyline"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/20" />
      </div>

      {/* Diagonal clip bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-white"
        style={{ clipPath: "polygon(0 100%, 100% 100%, 100% 0)" }} />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-40 pt-32">
        <div
          className={`transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <span className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase mb-6">
            <span className="w-6 h-px bg-cyan-400" />
            ABOUT US
          </span>
          <h1 className="text-5xl lg:text-8xl font-black text-white leading-none mb-6 tracking-tight">
            STREAM<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-200">
              SOLUTIONS
            </span>
          </h1>
          <p className="text-slate-300 text-lg max-w-xl leading-relaxed">
            香港を拠点に、日本の決済管理・資金管理業務を革新するグローバルフィンテック企業
          </p>
        </div>
      </div>
    </section>
  );
};

const MissionSection = () => {
  const { ref, visible } = useInView(0.1);

  return (
    <section ref={ref} className="relative bg-white overflow-hidden">
      {/* Diagonal top already done by hero clip */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        {/* Big number background */}
        <div className="absolute right-0 top-0 text-[20rem] font-black text-slate-50 leading-none select-none pointer-events-none overflow-hidden">
          01
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-0">
          {/* Left sticky label column */}
          <div className="lg:col-span-3 flex flex-col justify-start pt-2 mb-8 lg:mb-0">
            <div
              className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
            >
              <div className="w-1 h-16 bg-gradient-to-b from-cyan-500 to-transparent mb-4" />
              <span className="text-cyan-600 text-xs font-bold tracking-[0.3em] uppercase block mb-2">OUR MISSION</span>
              <span className="text-slate-300 text-xs tracking-widest">ミッション</span>
            </div>
          </div>

          {/* Right content */}
          <div className="lg:col-span-9">
            <div
              className={`transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <h2 className="text-4xl lg:text-6xl font-black text-slate-900 leading-tight mb-10">
                銀行振込の管理を、<br />
                <em className="not-italic text-cyan-600">もっと正確に、</em><br />
                <em className="not-italic text-cyan-600">もっとシンプルに。</em>
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              <div
                className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              >
                <p className="text-slate-600 text-base leading-relaxed mb-5">
                  当社は、日本国内における銀行振込を中心とした決済管理および資金管理業務の支援を行うとともに、これらを効率化するためのシステム開発・提供を行っております。
                </p>
                <p className="text-slate-600 text-base leading-relaxed">
                  業務フロー全体を最適化するため、単なる業務代行にとどまらず、自社開発によるシステムを活用し、データ連携・自動化・可視化までを一体的に設計・提供しております。
                </p>
              </div>

              {/* Image with diagonal clip */}
              <div
                className={`transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
              >
                <div className="relative h-72 lg:h-80 overflow-hidden rounded-2xl"
                  style={{ clipPath: "polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)" }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract-digital-financial-data-visualization-glowing-cyan-network-nodes-dark-background-flowing-data-streams-payment-processing-concept-futuristic-fintech-no-people-clean&width=700&height=500&seq=about_mission_v2&orientation=landscape"
                    alt="Mission visual"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/30 to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatsSection = () => {
  const { ref, visible } = useInView(0.2);

  return (
    <section
      ref={ref}
      className="relative py-24 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0f172a 0%, #0c1a2e 50%, #0f172a 100%)" }}
    >
      {/* Diagonal top */}
      <div className="absolute top-0 left-0 right-0 h-20 bg-white"
        style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }} />
      {/* Diagonal bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-white"
        style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }} />

      {/* Grid bg */}
      <div className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "linear-gradient(rgba(6,182,212,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.4) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1">
          {values.map((v, i) => (
            <div
              key={v.label}
              className={`text-center px-6 py-10 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <div className="text-5xl lg:text-6xl font-black text-cyan-400 mb-2 tabular-nums">{v.num}</div>
              <div className="text-white font-bold text-sm mb-2">{v.label}</div>
              <div className="text-slate-400 text-xs leading-relaxed">{v.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TimelineSection = () => {
  const { ref, visible } = useInView(0.05);

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className={`mb-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="absolute right-10 text-[12rem] font-black text-slate-50 leading-none select-none pointer-events-none hidden lg:block">02</div>
          <div className="w-1 h-16 bg-gradient-to-b from-cyan-500 to-transparent mb-4" />
          <span className="text-cyan-600 text-xs font-bold tracking-[0.3em] uppercase block mb-3">HISTORY</span>
          <h2 className="text-4xl lg:text-5xl font-black text-slate-900">沿革</h2>
        </div>

        {/* Alternating timeline */}
        <div className="relative">
          {/* Center vertical line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/60 via-cyan-400/20 to-transparent -translate-x-1/2" />

          <div className="space-y-0">
            {milestones.map((m, i) => {
              const isLeft = i % 2 === 0;
              return (
                <div
                  key={m.year}
                  className={`relative flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-0 py-8 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                  style={{ transitionDelay: `${0.1 + i * 0.1}s` }}
                >
                  {/* Left side */}
                  <div className={`lg:w-1/2 ${isLeft ? "lg:pr-16 lg:text-right" : "lg:pr-0 lg:pl-16 lg:order-2"}`}>
                    {isLeft ? (
                      <div className="lg:ml-auto">
                        <div className="inline-flex items-center gap-2 mb-2">
                          <span className="text-3xl font-black text-cyan-500">{m.year}</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">{m.title}</h3>
                        <p className="text-slate-500 text-sm leading-relaxed">{m.desc}</p>
                      </div>
                    ) : (
                      <div className="hidden lg:block" />
                    )}
                  </div>

                  {/* Center dot */}
                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-5 h-5 items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-cyan-500 ring-4 ring-cyan-500/20" />
                  </div>

                  {/* Right side */}
                  <div className={`lg:w-1/2 ${isLeft ? "lg:pl-16 lg:order-2" : "lg:pl-16"}`}>
                    {!isLeft ? (
                      <div>
                        <div className="inline-flex items-center gap-2 mb-2">
                          <span className="text-3xl font-black text-cyan-500">{m.year}</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">{m.title}</h3>
                        <p className="text-slate-500 text-sm leading-relaxed">{m.desc}</p>
                      </div>
                    ) : (
                      <div className="hidden lg:block" />
                    )}
                  </div>

                  {/* Mobile layout */}
                  <div className="lg:hidden flex items-start gap-4 w-full">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-cyan-500 ring-4 ring-cyan-500/20 mt-1 flex-shrink-0" />
                      <div className="w-px flex-1 bg-cyan-200 mt-2" />
                    </div>
                    <div className="pb-4">
                      <span className="text-2xl font-black text-cyan-500 block mb-1">{m.year}</span>
                      <h3 className="text-base font-bold text-slate-900 mb-1">{m.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const CompanyInfoSection = () => {
  const { ref, visible } = useInView(0.1);

  return (
    <section
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "#f8fafc" }}
    >
      {/* Diagonal top */}
      <div className="absolute top-0 left-0 right-0 h-20 bg-white"
        style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left: label + image */}
          <div className="lg:col-span-5">
            <div
              className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
            >
              <div className="w-1 h-16 bg-gradient-to-b from-cyan-500 to-transparent mb-4" />
              <span className="text-cyan-600 text-xs font-bold tracking-[0.3em] uppercase block mb-3">COMPANY INFO</span>
              <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-10">会社情報</h2>

              {/* Image with skew */}
              <div
                className="relative h-64 overflow-hidden rounded-2xl"
                style={{ clipPath: "polygon(0 0, 100% 0, 95% 100%, 5% 100%)" }}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=hong-kong-kowloon-yau-tong-industrial-area-modern-building-exterior-urban-street-view-clean-architecture-daytime-no-people-professional-corporate&width=700&height=450&seq=about_hq_v2&orientation=landscape"
                  alt="Stream Solutions HQ"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                <div className="absolute bottom-4 left-6 text-white">
                  <div className="text-xs text-slate-300 mb-1">Headquarters</div>
                  <div className="text-sm font-semibold">Yau Tong, Kowloon, HK</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: table */}
          <div className="lg:col-span-7">
            <div
              className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
            >
              <div className="space-y-0 rounded-2xl overflow-hidden border border-slate-200">
                {companyInfo.map((item, i) => (
                  <div
                    key={item.label}
                    className={`flex flex-col sm:flex-row group hover:bg-cyan-50/50 transition-colors duration-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}
                  >
                    <div className="sm:w-44 px-6 py-5 flex-shrink-0 border-b sm:border-b-0 sm:border-r border-slate-100">
                      <div className="text-xs font-bold text-slate-900 tracking-wide">{item.label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{item.labelEn}</div>
                    </div>
                    <div className="flex-1 px-6 py-5">
                      <p className="text-sm text-slate-700 leading-relaxed">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 h-56">
                <iframe
                  title="Stream Solutions Limited Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3692.0!2d114.2350!3d22.2950!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3404007b5e5e5e5e%3A0x0!2sSunray+Industrial+Centre%2C+610+Cha+Kwo+Ling+Rd%2C+Yau+Tong!5e0!3m2!1sja!2shk!4v1700000000000!5m2!1sja!2shk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CtaBanner = () => {
  const { t } = useTranslation();
  const { ref, visible } = useInView(0.2);

  return (
    <section ref={ref} className="relative py-32 overflow-hidden bg-slate-950">
      {/* Diagonal top */}
      <div className="absolute top-0 left-0 right-0 h-20"
        style={{ background: "#f8fafc", clipPath: "polygon(0 0, 100% 0, 0 100%)" }} />

      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract-technology-dark-background-glowing-cyan-circuit-patterns-data-flow-visualization-deep-space-atmosphere-futuristic-fintech-no-people-ultra-wide&width=1920&height=700&seq=about_cta_v2&orientation=landscape"
          alt="cta bg"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-slate-950/70" />
      </div>

      <div
        className={`relative z-10 max-w-4xl mx-auto px-6 text-center transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <div className="text-6xl lg:text-8xl font-black text-white/5 select-none mb-[-2rem]">CONTACT</div>
        <h2 className="text-3xl lg:text-5xl font-black text-white mb-6 relative z-10">{t("top.cta.title")}</h2>
        <p className="text-slate-400 text-base lg:text-lg max-w-xl mx-auto mb-10 leading-relaxed">{t("top.cta.sub")}</p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-3 px-10 py-5 bg-cyan-500 hover:bg-cyan-400 text-white font-bold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-base group"
        >
          {t("top.cta.btn")}
          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
        </Link>
      </div>
    </section>
  );
};

const AboutPage = () => {
  useSEO({
    title: "会社概要 | 香港九龍油塘・決済管理フィンテック企業",
    description: "Stream Solutions Limited会社概要。代表：山内ゆり。香港九龍油塘茶果嶺道610號 生利工業大廈2樓4室。銀行振込の決済管理・資金管理・システム開発を提供するフィンテック企業です。",
    keywords: "Stream Solutions Limited,会社概要,香港フィンテック,決済管理会社,山内ゆり,油塘",
    canonical: "/about",
    ogTitle: "会社概要 | Stream Solutions Limited",
    ogDescription: "香港を拠点に、日本の決済管理・資金管理業務を革新するフィンテック企業。代表：山内ゆり。",
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <HeroSection />
        <MissionSection />
        <StatsSection />
        <TimelineSection />
        <CompanyInfoSection />
        <CtaBanner />
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
